import React, { useEffect, useState } from "react";
import { api, formatBRL, formatDateTimeBR } from "../lib/api";
import { X, Loader2, Undo2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const KIND_LABEL = {
  parcial: "Pagamento parcial",
  quitacao: "Quitação",
  renovacao: "Renovação",
};

const REVERSAL_KIND_LABEL = {
  payment_partial: "Pagamento parcial",
  quittance: "Quitação",
  renewal: "Renovação",
  cancel: "Cancelamento",
  conversion: "Conversão",
};

export default function ReversalModal({ contract, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [lastPayment, setLastPayment] = useState(null);

  // Identifica o último lançamento não-reversed
  useEffect(() => {
    if (!contract) return;
    // Pega de todos ciclos (não só atual), o payment mais recente NÃO reversed
    const allPayments = [];
    (contract.cycles || []).forEach((cy) => {
      (cy.payments || []).forEach((p) => {
        if (!p.reversed) allPayments.push({ ...p, cycle_number: cy.cycle_number });
      });
    });
    allPayments.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    setLastPayment(allPayments[0] || null);
  }, [contract]);

  // Determina o tipo de estorno conforme o estado atual
  const status = contract?.status;
  let mode = "payment"; // default
  if (status === "cancelado") mode = "cancel";
  else if (status === "convertido") mode = "conversion";

  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 3) {
      toast.error("Motivo é obrigatório (mín. 3 caracteres)");
      return;
    }
    setSaving(true);
    try {
      let url = "";
      if (mode === "cancel") url = `/contracts/${contract.id}/reverse-cancel`;
      else if (mode === "conversion") url = `/contracts/${contract.id}/reverse-conversion`;
      else url = `/contracts/${contract.id}/reverse-last-payment`;
      await api.post(url, { reason: reason.trim() });
      toast.success("Lançamento estornado com sucesso");
      onDone && onDone();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha ao estornar");
    } finally {
      setSaving(false);
    }
  };

  const renderTarget = () => {
    if (mode === "cancel") {
      return (
        <div className="text-[12.5px] text-[#D4D4D4]">
          Contrato será reaberto: <strong className="text-amber-300">cancelado → ativo</strong>.
          O ciclo cancelado volta a aberto. Status do cliente é restaurado.
        </div>
      );
    }
    if (mode === "conversion") {
      return (
        <div className="text-[12.5px] text-[#D4D4D4]">
          Conversão para <strong className="text-violet-300">{contract.converted_to_kind || "parcelado/acordo"}</strong> será desfeita.
          O parcelado/acordo será apagado (só é permitido se não há parcelas pagas).
          Contrato original volta a ativo.
        </div>
      );
    }
    if (!lastPayment) {
      return <div className="text-[12.5px] text-[#737373]">Nenhum lançamento para estornar.</div>;
    }
    return (
      <div className="space-y-1.5 text-[12.5px]">
        <div className="flex justify-between">
          <span className="text-[#737373]">Tipo:</span>
          <span className="text-white font-semibold">{KIND_LABEL[lastPayment.type] || lastPayment.type}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-[#737373]">Valor:</span>
          <span className="font-mono text-[#D4AF37]">{formatBRL(lastPayment.amount)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-[#737373]">Data:</span>
          <span className="text-[#D4D4D4]">{formatDateTimeBR(lastPayment.created_at)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-[#737373]">Ciclo:</span>
          <span className="text-[#D4D4D4]">#{lastPayment.cycle_number}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-[#737373]">Por:</span>
          <span className="text-[#D4D4D4]">{lastPayment.recorded_by_email}</span>
        </div>
      </div>
    );
  };

  const canSubmit = mode !== "payment" || lastPayment;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="reversal-modal">
      <div className="fc-card w-full max-w-lg my-8 p-6">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-red-300 flex items-center gap-1.5">
              <Undo2 size={11}/> Correção administrativa
            </div>
            <h2 className="font-display text-2xl font-bold mt-1">Estornar lançamento</h2>
          </div>
          <button onClick={onClose} className="fc-btn-ghost" data-testid="close-reversal-modal">
            <X size={16}/>
          </button>
        </div>

        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 mb-4 flex gap-2">
          <AlertTriangle size={14} className="text-amber-300 flex-shrink-0 mt-0.5"/>
          <div className="text-[11.5px] text-amber-200/90 leading-relaxed">
            O lançamento original NÃO será apagado — fica marcado como estornado.
            Auditoria preservada (quem, quando, motivo).
          </div>
        </div>

        <div className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] p-3 mb-4">
          <div className="text-[10px] uppercase tracking-wider text-[#737373] mb-2">Será estornado</div>
          {renderTarget()}
        </div>

        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-[#737373]">Motivo (obrigatório)</span>
            <textarea
              className="fc-input mt-1"
              rows="3"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              minLength={3}
              maxLength={500}
              placeholder="Ex: lancei renovação por engano, era pagamento parcial"
              data-testid="reversal-reason"
            />
          </label>
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#1f1f1f]">
            <button type="button" onClick={onClose} className="fc-btn-ghost" data-testid="cancel-reversal">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || !canSubmit}
              className="fc-btn-gold flex items-center gap-2 bg-red-500/10 border-red-500/40 text-red-200 hover:bg-red-500/20"
              data-testid="submit-reversal"
            >
              {saving ? <><Loader2 size={14} className="animate-spin"/> Estornando…</> : <><Undo2 size={14}/> Confirmar estorno</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export { REVERSAL_KIND_LABEL };
